import { useCallback, useMemo, useState } from 'react';

import type { HistoryItem, InteractionHistoryActions } from '@poseidon/components/ContactDetail';
import { interactionHistoryId } from '@poseidon/components/ContactDetail';
import { Button } from '@poseidon/components/Input2';

import { groupHistorySteps } from '~/components/Sequence/sequenceHistory';
import { SequenceHistoryPopover } from '~/components/Sequence/SequenceHistoryPopover';
import { HISTORY_STEP_ROW_H, SequenceHistoryStep } from '~/components/Sequence/SequenceHistoryStep';

/** How many of the newest steps show before "Show earlier" — the canvas opens on NOW, just below these. */
export const RECENT_HISTORY_COUNT = 6;
const HEADER_H = 44;

interface Props {
  /** The contact's loaded history, oldest first (the order the feed shows it). */
  items: HistoryItem[];
  actions: InteractionHistoryActions;
  /** The canvas spine's x and the content width, so the history sits on the same line as the sequence. */
  spineX: number;
  width: number;
  /** The server holds history older than `items` — "Load older" (shown once every loaded step is) fetches it. */
  hasOlder?: boolean;
  loadingOlder?: boolean;
  /** The last "Load older" failed — the button says so and retries it. */
  olderFailed?: boolean;
  onLoadOlder?: () => void;
}

/**
 * The contact's history at the top of the sequence canvas — the past flowing down into the sequence, drawn as
 * steps like the graph's own ({@link SequenceHistoryStep}): one per entry, or per conversation (back-to-back
 * messages fold into one, see {@link groupHistorySteps}), newest last (right above Prep). The newest
 * {@link RECENT_HISTORY_COUNT} show; the rest fold behind "Show earlier" so a long history doesn't stretch the
 * canvas out of reach, and "Load older" pages in what the contact panel didn't load. A step opens its entry or
 * thread in a popover.
 */
export function SequenceHistoryColumn({
  items,
  actions,
  spineX,
  width,
  hasOlder = false,
  loadingOlder = false,
  olderFailed = false,
  onLoadOlder,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  const [opened, setOpened] = useState<{ id: string; anchor: Element } | null>(null);
  const steps = useMemo(
    () =>
      groupHistorySteps(items, actions.currentUserId).map((step) => ({
        ...step,
        id: interactionHistoryId(step.entries[0]!),
      })),
    [items, actions.currentUserId],
  );
  const shown = showAll ? steps : steps.slice(-RECENT_HISTORY_COUNT);
  const folded = steps.length - shown.length;
  const openedStep = opened == null ? undefined : steps.find((s) => s.id === opened.id);
  const close = useCallback(() => setOpened(null), []);

  return (
    <div data-history-column className="relative flex flex-col gap-5 pb-2" style={{ width, paddingTop: HEADER_H }}>
      {/* The spine through the past: from the first step's tile down to the canvas below, where it meets Prep. */}
      <span
        aria-hidden
        className="pointer-events-none absolute w-0.5 bg-lynch-300 dark:bg-slate-600"
        style={{ left: spineX - 1, top: HEADER_H + HISTORY_STEP_ROW_H / 2, bottom: 0 }}
      />
      <div
        data-no-pan
        className="absolute flex items-center gap-2 whitespace-nowrap"
        style={{ left: spineX, top: 10, transform: 'translate(-50%, 0)' }}>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-lynch-500 dark:text-lynch-400">
          Activity
        </span>
        {folded > 0 && (
          <Button size="2xs" text onClick={() => setShowAll(true)}>
            Show {folded} earlier
          </Button>
        )}
        {folded === 0 && hasOlder && onLoadOlder != null && (
          <Button
            size="2xs"
            text
            status={loadingOlder ? 'updating' : undefined}
            disabled={loadingOlder}
            onClick={() => {
              // What it loads lands above the steps already shown — keep it in view rather than folded away.
              setShowAll(true);
              onLoadOlder();
            }}>
            {olderFailed ? 'Retry loading older' : 'Load older'}
          </Button>
        )}
        {showAll && steps.length > RECENT_HISTORY_COUNT && (
          <Button size="2xs" text onClick={() => setShowAll(false)}>
            Show fewer
          </Button>
        )}
      </div>
      {shown.map((step) => (
        <SequenceHistoryStep
          key={step.id}
          entryId={step.id}
          summary={step.summary}
          spineX={spineX}
          width={width}
          open={opened?.id === step.id}
          onOpen={(id, anchor) => setOpened({ id, anchor })}
        />
      ))}
      {opened != null && openedStep != null && (
        <SequenceHistoryPopover
          // A fresh popover per step: a thread opens on its newest message (see the popover).
          key={opened.id}
          entries={openedStep.entries}
          title={openedStep.entries.length > 1 ? openedStep.summary.title : undefined}
          actions={actions}
          anchor={opened.anchor}
          onClose={close}
        />
      )}
    </div>
  );
}
